// frontend/src/components/layout/mobile/StoreSwitcher.jsx
//
// The phone's store switcher: the top bar's current store, and the sheet it
// opens listing every store the merchant can open, the current one ticked.
// Picking another one switches the session (useStoreSwitch); the page restarts
// on the new store, so the row keeps its spinner until it goes. With one store
// there is nothing to switch to, and the store is shown without a button.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON, Check } from '../../../icons';
import DisclosureCaret from '../../common/DisclosureCaret';
import { useAuth } from '../../../contexts/AuthContext';
import { avatarColor, initials, sessionIdentity, storeDisplayName, storeDomainIsDistinct } from '../../../utils/avatar';
import { useStoreSwitch } from '../useStoreSwitch';
import StoreChip from '../StoreChip';
import BottomSheet from './BottomSheet';
import '../../../styles/avatar-chip.css';

export default function StoreSwitcher() {
  const { t } = useTranslation();
  const { user, stores, selectedStore } = useAuth();
  const { canSwitch, switchTo, switchingId, error, confirmDialog } = useStoreSwitch();
  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);
  const identity = sessionIdentity(user, selectedStore);
  const title = t('storeSwitcher.title');

  const shown = (
    <>
      <span className="avatar-chip avatar-chip--sm" style={{ background: avatarColor(identity.seed) }}>
        {initials(identity.label)}
      </span>
      <span className="mobile-store-button__name">{identity.label}</span>
    </>
  );

  if (!canSwitch) return <div className="mobile-store-button">{shown}</div>;

  return (
    <>
      <button
        type="button"
        className="mobile-store-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {shown}
        <DisclosureCaret open={open} size={ICON.md} weight="bold" className="mobile-store-button__caret" />
      </button>
      <BottomSheet open={open} onClose={onClose} label={title}>
        <section className="sheet-section">
          <h2 className="sheet-section__title">{title}</h2>
          <ul className="sheet-list">
            {stores.map((store) => {
              const isCurrent = store.id === selectedStore?.id;
              const switching = store.id === switchingId;
              return (
                <li key={store.id}>
                  <button
                    type="button"
                    className={`sheet-row${isCurrent ? ' sheet-row--active' : ''}`}
                    aria-current={isCurrent ? 'true' : undefined}
                    aria-busy={switching ? 'true' : undefined}
                    disabled={switchingId !== null}
                    onClick={() => (isCurrent ? onClose() : switchTo(store.id))}
                  >
                    <StoreChip store={store} />
                    <span className="sheet-row__text">
                      <span className="sheet-row__primary">{storeDisplayName(store)}</span>
                      {storeDomainIsDistinct(store) && (
                        <span className="sheet-row__secondary">{store.domain}</span>
                      )}
                    </span>
                    <span className="sheet-row__trail">
                      {switching && <span className="spinner-inline" />}
                      {isCurrent && <Check size={ICON.lg} weight="bold" />}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {error && <p className="sheet-alert" role="alert">{error}</p>}
        </section>
      </BottomSheet>
      {confirmDialog}
    </>
  );
}
